/**
 * Prices the builder's brief as it is edited: 500 ms after the last change,
 * once the draft is complete. Each body is quoted at most once (by its JSON
 * key), a newer request aborts the one in flight, and a response only lands
 * if no newer request has started. Whether the shown quote is current is
 * derived from the key it priced, never stored, so it can't get stuck on
 * "Updating…". Nothing retries on its own: an error waits for Retry or the
 * next edit, and a 429 pauses quoting for a minute so the per-IP API limit
 * isn't spent re-asking.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { quoteMediaPlan, type QuoteOutcome } from '../../../services/mediaPlansApi';
import type { MediaPlanQuote, NeedsAdvertiserStub, PlanInput } from './types';

export const LIVE_QUOTE_DELAY_MS = 500;
export const RATE_LIMIT_PAUSE_MS = 60_000;

export type LiveQuoteStatus =
  /** The draft can't be priced yet (see the builder's checks). */
  | 'incomplete'
  /** Waiting out the debounce, or a quote is on its way. */
  | 'updating'
  /** `quote` prices exactly the current draft. */
  | 'current'
  /** The advertiser isn't in the registry yet. */
  | 'needs-advertiser'
  /** The advertiser name matches several records. */
  | 'ambiguous'
  | 'error'
  /** Too many requests: quoting resumes on its own after a minute. */
  | 'paused';

export interface LiveQuote {
  status: LiveQuoteStatus;
  /** The last quote that came back — kept while a newer one is on its way. */
  quote: MediaPlanQuote | null;
  /** The body `quote` priced. Create sends exactly this, with the quote's fingerprint. */
  quotedInput: PlanInput | null;
  needsAdvertiser: NeedsAdvertiserStub | null;
  ambiguous: { error: string; candidates: Array<{ key: string; name: string }> } | null;
  error: string | null;
  /** Quote the current draft again now. */
  retry: () => void;
  /** Show a quote that came back some other way (create's 409 `quote_changed`). */
  replaceQuote: (quote: MediaPlanQuote, input: PlanInput) => void;
}

interface Landed {
  key: string;
  input: PlanInput;
  outcome: QuoteOutcome | { kind: 'error'; message: string };
}

const keyOf = (input: PlanInput): string => JSON.stringify(input);

const isAbort = (err: unknown): boolean => !!err && typeof err === 'object' && (err as { name?: string }).name === 'AbortError';

const errorText = (err: unknown): string => (err instanceof Error && err.message ? err.message : 'The quote could not be loaded.');

/** `input` null = the draft is incomplete, so nothing is quoted. */
export function useLiveQuote(input: PlanInput | null, opts: { delayMs?: number } = {}): LiveQuote {
  const delayMs = opts.delayMs ?? LIVE_QUOTE_DELAY_MS;
  const key = input ? keyOf(input) : null;
  const [landed, setLanded] = useState<Landed | null>(null);
  const [lastQuote, setLastQuote] = useState<{ quote: MediaPlanQuote; input: PlanInput } | null>(null);
  const [pausedUntil, setPausedUntil] = useState<number | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const generation = useRef(0);
  const inflight = useRef<{ key: string; controller: AbortController } | null>(null);
  const inputRef = useRef(input);
  inputRef.current = input;
  const mounted = useRef(true);
  // Set on every mount: StrictMode runs this cleanup once on its simulated unmount in development.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      inflight.current?.controller.abort();
    };
  }, []);

  const run = useCallback((runKey: string, body: PlanInput) => {
    inflight.current?.controller.abort();
    const controller = new AbortController();
    const gen = ++generation.current;
    inflight.current = { key: runKey, controller };
    quoteMediaPlan(body, { signal: controller.signal }).then(
      (outcome) => {
        if (!mounted.current || gen !== generation.current) return;
        inflight.current = null;
        setLanded({ key: runKey, input: body, outcome });
        if (outcome.kind === 'quote') setLastQuote({ quote: outcome.quote, input: body });
      },
      (err) => {
        if (!mounted.current || gen !== generation.current || isAbort(err)) return;
        inflight.current = null;
        if ((err as { status?: number })?.status === 429) {
          setPausedUntil(Date.now() + RATE_LIMIT_PAUSE_MS);
          setLanded(null);
          return;
        }
        setLanded({ key: runKey, input: body, outcome: { kind: 'error', message: errorText(err) } });
      },
    );
  }, []);

  // Quote each new body once, after the debounce.
  useEffect(() => {
    if (!key || pausedUntil !== null) return;
    if (landed?.key === key) {
      // Back to a body already priced (A → B → A): what's in flight is for another body.
      if (inflight.current) {
        inflight.current.controller.abort();
        inflight.current = null;
        generation.current += 1;
      }
      return;
    }
    if (inflight.current?.key === key) return;
    const timer = setTimeout(() => {
      if (inputRef.current) run(key, inputRef.current);
    }, delayMs);
    return () => clearTimeout(timer);
  }, [key, landed, pausedUntil, retryCount, delayMs, run]);

  // A 429 pause ends on its own.
  useEffect(() => {
    if (pausedUntil === null) return;
    const timer = setTimeout(() => setPausedUntil(null), Math.max(0, pausedUntil - Date.now()));
    return () => clearTimeout(timer);
  }, [pausedUntil]);

  // A draft that became incomplete has nothing in flight worth waiting for.
  useEffect(() => {
    if (key === null && inflight.current) {
      inflight.current.controller.abort();
      inflight.current = null;
      generation.current += 1;
    }
  }, [key]);

  const retry = useCallback(() => {
    if (pausedUntil !== null) return;
    setLanded(null);
    setRetryCount((n) => n + 1);
  }, [pausedUntil]);

  const replaceQuote = useCallback((quote: MediaPlanQuote, body: PlanInput) => {
    inflight.current?.controller.abort();
    inflight.current = null;
    generation.current += 1;
    setLanded({ key: keyOf(body), input: body, outcome: { kind: 'quote', quote } });
    setLastQuote({ quote, input: body });
  }, []);

  const current = landed && landed.key === key ? landed.outcome : null;
  let status: LiveQuoteStatus;
  if (!key) status = 'incomplete';
  else if (pausedUntil !== null) status = 'paused';
  else if (!current) status = 'updating';
  else if (current.kind === 'quote') status = 'current';
  else if (current.kind === 'needsAdvertiser') status = 'needs-advertiser';
  else if (current.kind === 'ambiguous') status = 'ambiguous';
  else status = 'error';

  return {
    status,
    quote: lastQuote?.quote ?? null,
    quotedInput: lastQuote?.input ?? null,
    needsAdvertiser: current?.kind === 'needsAdvertiser' ? current.needsAdvertiser : null,
    ambiguous: current?.kind === 'ambiguous' ? { error: current.error, candidates: current.candidates } : null,
    error: current?.kind === 'error' ? current.message : null,
    retry,
    replaceQuote,
  };
}
